<script setup lang="ts">
import { useApplicationStore } from '@/models/application-store';

import { EventBus } from '@/components/replay/model/types';
import { useEventBus } from '@/components/replay/model';

import DialogElement from '@/components/dialog.vue';

import { deleteReplay } from '../model';

const { emit: emitBus } = useEventBus();

const store = useApplicationStore();
const { dialogs } = storeToRefs(store);

const { addErrorToast, addSuccessToast } = useToast();

const deleteReplayDialog = computed(() => dialogs.value.deleteReplay);
const currentReplay = computed(() => deleteReplayDialog.value.replayData);
const initiatorId = computed(() => deleteReplayDialog.value.initiator);

const handleConfirm = async () => {
  deleteReplayDialog.value.isShow = false;

  const { status, message } = await deleteReplay(currentReplay.value?.id ?? 0);

  const defaultMessage = status ? { title: 'Реплей удалён' } : { title: 'Неизвестная ошибка' };
  const toastMessage = message ?? defaultMessage;

  const toast = status ? addSuccessToast : addErrorToast;
  toast(toastMessage);
  emitBus<{ replayId: number; initiatorId: symbol | null }>(EventBus.Types.DELETE, {
    replayId: currentReplay.value?.id ?? 0,
    initiatorId: initiatorId.value,
  });
};

const deleteButtonText = computed(() =>
  // @TODO: add animation
  !dialogs.value.deleteReplay.isRequest ? 'Удалить' : 'Удаляем'
);
</script>

<template>
  <dialog-element
    v-model="dialogs.deleteReplay.isShow"
    class="modal-dialog"
  >
    <div class="delete-wrap round text-md shadow-w-l">
      <div>
        <h2>Удалить реплей?</h2>
        <p>
          Реплей <span class="contrast fw-700">{{ currentReplay?.name }}</span> будет удалён.
        </p>
        <p>После удаления восстановить реплей будет невозможно</p>
      </div>

      <div class="row flex-between">
        <button
          class="button md third"
          @click="deleteReplayDialog.isShow = false"
        >
          Отмена
        </button>
        <button
          class="button md primary"
          :class="{ 'is-loading': dialogs.deleteReplay.isRequest }"
          :disabled="dialogs.deleteReplay.isRequest"
          @click="handleConfirm()"
        >
          {{ deleteButtonText }}
        </button>
      </div>
    </div>
  </dialog-element>
</template>

<style scoped lang="postcss">
.modal-dialog {
  place-items: center;

  &[open] {
    display: grid;
  }

  &:deep(.wrap) {
    width: auto;
    position: relative;
  }

  &:deep(.head .g-icon.close-btn) {
    --pos: 24px;
    --size: 32px;
    height: var(--size);
    width: var(--size);
    transform: translate(0, 0);
    top: var(--pos);
    right: var(--pos);
    padding: 0;
  }
}

.row {
  gap: var(--space-s-var_01);

  .button {
    flex: 1 1 auto;
    line-height: 1;
  }
}

.delete-wrap {
  --spacing: 24px;
  --color-accient: var(--color-accient-sea-blue-normal);

  color: var(--color-neutral-gray-20);

  justify-self: center;
  display: flex;
  flex-direction: column;
  padding: var(--spacing);
  gap: var(--spacing);

  border-top: 4px solid var(--color-accient);
  background:
    radial-gradient(
      131.77% 71.31% at 0.61% 1.39%,
      rgb(0 119 254 / 15%) 0%,
      rgb(0 119 254 / 0) 54.19%,
      rgb(0 119 254 / 0) 100%
    ),
    var(--color-bg-dark-blue);

  h2 {
    color: var(--color-white-100);
    margin-bottom: var(--space-s-var_02);
  }

  .contrast {
    color: var(--color-white-100);
  }
}
</style>
